import { DOCUMENT } from '@angular/common';
import { Injectable, inject } from '@angular/core';

/**
 * Returns keyboard focus to whatever opened a dialog once it closes (QA
 * accessibility pass): closing with Escape used to drop focus on <body>, which
 * sends a keyboard user back to the top of the page.
 *
 * It watches the DOM instead of every dialog: PrimeNG appends them to <body>
 * from a dozen different components, so a single observer covers all of them
 * without touching each template.
 */
@Injectable({ providedIn: 'root' })
export class DialogFocusService {
	private readonly document = inject(DOCUMENT);

	/** Last element focused outside any dialog — the trigger we come back to. */
	private trigger: HTMLElement | null = null;
	private observer: MutationObserver | null = null;

	private static readonly DIALOG_SELECTOR = '.p-dialog, .p-confirmdialog';

	start(): void {
		if (this.observer) return;

		this.document.addEventListener(
			'focusin',
			(event) => {
				const target = event.target as HTMLElement | null;
				if (target && !target.closest(DialogFocusService.DIALOG_SELECTOR)) {
					this.trigger = target;
				}
			},
			true,
		);

		this.observer = new MutationObserver((mutations) => {
			const closed = mutations.some((mutation) =>
				[...mutation.removedNodes].some(
					(node) =>
						node instanceof HTMLElement &&
						(node.matches?.(DialogFocusService.DIALOG_SELECTOR) ||
							node.querySelector?.(DialogFocusService.DIALOG_SELECTOR)),
				),
			);
			if (!closed) return;
			// Another dialog may still be open (a confirm on top of a form)
			if (this.document.querySelector(DialogFocusService.DIALOG_SELECTOR)) return;
			this.restore();
		});
		this.observer.observe(this.document.body, { childList: true, subtree: true });
	}

	private restore(): void {
		const target = this.trigger;
		if (!target?.isConnected) return;
		// After the dialog's own teardown, or PrimeNG blurs it again
		setTimeout(() => target.focus({ preventScroll: true }));
	}
}
